import { t } from "@lingui/core/macro";
import { useMemo } from "react";
import { LimeSelect, LimeSelectProps } from "./LimeSelect";
import { buildServiceSelectItems } from "./serviceSelectItems";
import { api } from "@/lib/api-client";
import type { GetServices } from "@/server-types";

type Service = GetServices["response"]["services"][number];

export type LimeServiceSelectProps = Omit<
  LimeSelectProps,
  | "items"
  | "value"
  | "onChange"
  | "selectionMode"
  | "onSingleItemChange"
  | "onMultipleItemChange"
> & {
  value: number[];
  onChange: (serviceIds: number[]) => void;
  query?: GetServices["query"];
  filterService?: (service: Service) => boolean;
};

/**
 * Multi-select over the client's services, grouped into their categories with
 * a per-category select-all and a search box. `value`/`onChange` speak in
 * service ids; the service list is fetched (and cache-shared) internally.
 */
export function LimeServiceSelect({
  value,
  onChange,
  query,
  filterService,
  isLoading,
  ...props
}: LimeServiceSelectProps) {
  const { data: servicesData, isLoading: isServicesLoading } =
    api.service.useGetServices(query ?? {});

  // Read outside the memo so switching language re-labels the fallback
  // section instead of keeping whatever locale was active on first render.
  const uncategorizedSectionName = t`Brez kategorije`;

  const items = useMemo(
    () =>
      buildServiceSelectItems(
        (servicesData?.services ?? []).filter(
          (service) => filterService?.(service) ?? true,
        ),
        uncategorizedSectionName,
      ),
    [servicesData, filterService, uncategorizedSectionName],
  );

  return (
    <LimeSelect
      label={t`Storitve`}
      placeholder={t`Izberi storitve`}
      disallowEmptySelection={false}
      isSearchable
      searchPlaceholder={t`Išči storitve`}
      showSelectAllToggle
      showSectionSelectAllToggle
      renderValue={(selected) => {
        const first2 = selected.slice(0, 2);
        const rest = selected.slice(2);
        return (
          first2.map((item) => item.rendered).join(", ") +
          (rest.length > 0 ? ` + ${rest.length}` : "")
        );
      }}
      {...props}
      items={items}
      isLoading={isLoading || isServicesLoading}
      selectionMode="multiple"
      value={value.map(String)}
      onMultipleItemChange={(keys) => {
        onChange(
          keys.flatMap((key) => {
            const serviceId = Number(key);
            return isNaN(serviceId) ? [] : [serviceId];
          }),
        );
      }}
    />
  );
}
